import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, XCircle, Pencil, PlusCircle, Trash2, Settings2, Sliders, PackageCheck, Activity } from "lucide-react";

const ACTION_META = {
  claim_approved: { icon: ShieldCheck, color: "text-[#1A6E4E]", bg: "bg-[#E3F3ED]", label: "Claim approved" },
  claim_rejected: { icon: XCircle, color: "text-[#9B2C2C]", bg: "bg-[#FBEAEA]", label: "Claim rejected" },
  facility_updated: { icon: Pencil, color: "text-[#0077B6]", bg: "bg-[#E3F0F7]", label: "Facility edited" },
  facility_created: { icon: PlusCircle, color: "text-[#1A6E4E]", bg: "bg-[#E3F3ED]", label: "Facility created" },
  facility_deleted: { icon: Trash2, color: "text-[#9B2C2C]", bg: "bg-[#FBEAEA]", label: "Facility deleted" },
  settings_updated: { icon: Settings2, color: "text-[#6B4FBB]", bg: "bg-[#EEE9FA]", label: "Settings updated" },
  config_updated: { icon: Sliders, color: "text-[#6B4FBB]", bg: "bg-[#EEE9FA]", label: "Pricing/integrations updated" },
  concierge_fulfilled: { icon: PackageCheck, color: "text-[#0077B6]", bg: "bg-[#E3F0F7]", label: "Concierge fulfilled" },
};

function summarize(log) {
  const d = log.details || {};
  if (log.action === "facility_updated" && d) {
    const keys = Object.keys(d);
    if (keys.length) return "Changed: " + keys.map((k) => (typeof d[k] === "boolean" ? `${k}=${d[k]}` : k)).join(", ");
  }
  if (log.action === "config_updated" && d.fields) return "Changed: " + Object.keys(d.fields).join(", ");
  if (log.action === "settings_updated" && d.fields) return "Changed: " + d.fields.join(", ");
  if (log.action === "claim_approved" && d.director) return `Director: ${d.director}`;
  return "";
}

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString() + " " + new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/audit", { params: { limit: 200 } })
      .then((r) => setLogs(r.data))
      .catch(() => toast.error("Could not load audit log"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 text-center text-[#5C6660]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="max-w-[900px]" data-testid="audit-log">
      <div className="flex items-center gap-2 mb-2"><Activity className="h-5 w-5 text-[#0077B6]" /><h2 className="font-heading text-2xl font-medium text-[#1C211F]">Audit log</h2></div>
      <p className="text-[#4A534E] mb-6">A running record of admin actions — claim reviews, tier/toggle changes, facility edits, pricing & integration changes, and concierge fulfillment.</p>
      {logs.length === 0 ? (
        <div className="bg-white border border-[#E8E6DF] rounded-2xl p-10 text-center text-[#5C6660]">No activity recorded yet.</div>
      ) : (
        <div className="bg-white border border-[#E8E6DF] rounded-2xl divide-y divide-[#F2F1EC]">
          {logs.map((log) => {
            const m = ACTION_META[log.action] || { icon: Activity, color: "text-[#5C6660]", bg: "bg-[#EFEEE8]", label: log.action };
            const Icon = m.icon;
            const detail = summarize(log);
            return (
              <div key={log.log_id} className="flex items-start gap-3 p-4" data-testid="audit-row">
                <div className={`h-9 w-9 rounded-xl ${m.bg} flex items-center justify-center shrink-0`}><Icon className={`h-4.5 w-4.5 ${m.color}`} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-[#1C211F]">{m.label}{log.target_name ? <span className="text-[#4A534E] font-normal"> — {log.target_name}</span> : ""}</span>
                    <span className="text-xs text-[#5C6660] shrink-0">{timeAgo(log.created_at)}</span>
                  </div>
                  {detail && <div className="text-xs text-[#5C6660] mt-0.5 break-words">{detail}</div>}
                  <div className="text-xs text-[#9AA39D] mt-0.5">by {log.actor_email || "system"}{log.actor_role ? ` (${log.actor_role})` : ""}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
